import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function AdminShop({ headers }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', condition: '', grade: '', cert_number: '', photo_urls: '', price_xrp: '', accept_fuzzy: false, price_fuzzy: '', quantity: '1', is_limited: false });
  const [trackingDrafts, setTrackingDrafts] = useState({});
  const [showCsv, setShowCsv] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [cfgForm, setCfgForm] = useState(null);

  const refresh = useCallback(() => {
    axios.get(`${API}/admin/shop/overview`, { headers }).then(r => setData(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { refresh(); }, [refresh]);

  const createListing = async () => {
    if (!form.title.trim() || !parseFloat(form.price_xrp)) { toast.error('Title and XRP price required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/shop/listings`, {
        ...form,
        photo_urls: form.photo_urls.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
        price_xrp: parseFloat(form.price_xrp),
        price_fuzzy: form.accept_fuzzy ? parseFloat(form.price_fuzzy) : null,
        quantity: Math.max(1, parseInt(form.quantity) || 1),
        is_limited: form.is_limited,
      }, { headers });
      toast.success('Listing created');
      setForm({ title: '', description: '', condition: '', grade: '', cert_number: '', photo_urls: '', price_xrp: '', accept_fuzzy: false, price_fuzzy: '', quantity: '1', is_limited: false });
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const removeListing = async (id) => {
    if (!window.confirm('Remove this listing?')) return;
    try { await axios.delete(`${API}/admin/shop/listings/${id}`, { headers }); toast.success('Removed'); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const resolveOffer = async (id, action) => {
    try {
      await axios.post(`${API}/admin/shop/offers/${id}/${action}`, {}, { headers });
      toast.success(action === 'accept' ? 'Offer accepted — buyer charged' : 'Offer declined');
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const shipEscrow = async (id) => {
    try {
      await axios.post(`${API}/admin/shop/escrows/${id}/ship`, { tracking_number: trackingDrafts[id] || '' }, { headers });
      toast.success('Marked shipped — buyer notified');
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const importCsv = async () => {
    if (!csvText.trim()) { toast.error('Paste CSV rows first'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/shop/listings/bulk`, { csv_text: csvText }, { headers });
      const { created, errors } = res.data;
      toast.success(`${created} cards imported${errors.length ? ` · ${errors.length} rows skipped` : ''}`);
      if (errors.length) errors.slice(0, 3).forEach(e => toast.error(e));
      setCsvText('');
      setShowCsv(false);
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const openConfig = async () => {
    if (showConfig) { setShowConfig(false); return; }
    try {
      const res = await axios.get(`${API}/shop/config`);
      const t = res.data.discount_tiers || [];
      setCfgForm({
        shipping_domestic: String(res.data.shipping_domestic),
        shipping_international: String(res.data.shipping_international),
        seller_fee_pct: String(res.data.seller_fee_pct ?? 9.85),
        t1_qty: String(t[0]?.min_qty ?? 5), t1_pct: String(t[0]?.pct ?? 5),
        t2_qty: String(t[1]?.min_qty ?? 10), t2_pct: String(t[1]?.pct ?? 10),
      });
      setShowConfig(true);
    } catch { toast.error('Could not load shop config'); }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/shop/config`, {
        shipping_domestic: parseFloat(cfgForm.shipping_domestic) || 0,
        shipping_international: parseFloat(cfgForm.shipping_international) || 0,
        seller_fee_pct: parseFloat(cfgForm.seller_fee_pct) || 9.85,
        discount_tiers: [
          { min_qty: parseInt(cfgForm.t1_qty) || 0, pct: parseFloat(cfgForm.t1_pct) || 0 },
          { min_qty: parseInt(cfgForm.t2_qty) || 0, pct: parseFloat(cfgForm.t2_pct) || 0 },
        ],
      }, { headers });
      toast.success('Shop settings saved');
      setShowConfig(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const active = (data?.listings || []).filter(l => l.status === 'active');
  const pendingApproval = (data?.listings || []).filter(l => l.status === 'pending_approval');

  const approveListing = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/shop/listings/${id}/approve`, {}, { headers });
      toast.success('Listing approved & announced');
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const rejectListing = async (id) => {
    const reason = window.prompt('Reason (optional):') || '';
    setBusy(true);
    try {
      await axios.post(`${API}/admin/shop/listings/${id}/reject`, { reason }, { headers });
      toast.success('Listing rejected');
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };
  const sold = (data?.listings || []).filter(l => l.status === 'sold').slice(0, 5);
  const inputCls = "h-9 text-xs bg-slate-900 border-slate-700 text-white placeholder:text-slate-600";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-white">Card Shop</p>
        <span className="text-[10px] text-slate-400">
          Revenue: <span className="text-cyan-300 font-semibold">{Number(data?.shop_revenue || 0).toFixed(2)} XRP</span>
          {(data?.shop_revenue_fuzzy || 0) > 0 && <span className="text-amber-300 font-semibold"> · {Number(data.shop_revenue_fuzzy).toLocaleString()} FUZZY</span>}
        </span>
      </div>
      <div className="rounded-xl bg-slate-800/50 border border-cyan-500/20 p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <Button data-testid="shop-new-listing-toggle" size="sm" onClick={() => setShowForm(v => !v)} className="bg-cyan-700 hover:bg-cyan-600 h-9 text-[10px]">
            {showForm ? 'Hide form' : '+ List a card'}
          </Button>
          <Button data-testid="shop-csv-toggle" size="sm" onClick={() => setShowCsv(v => !v)} className="bg-indigo-700 hover:bg-indigo-600 h-9 text-[10px]">
            {showCsv ? 'Hide CSV' : '📄 Bulk CSV'}
          </Button>
          <Button data-testid="shop-config-toggle" size="sm" onClick={openConfig} className="bg-slate-700 hover:bg-slate-600 h-9 text-[10px]">
            {showConfig ? 'Hide settings' : '⚙️ Ship & deals'}
          </Button>
        </div>
        {showCsv && (
          <div className="space-y-2">
            <p className="text-[10px] text-slate-400">
              Header row required. Columns: <span className="text-cyan-300 font-mono">title, price_xrp, quantity, grade, condition, cert_number, limited, photo_url, description</span>.
              Mark limited cards with <span className="text-amber-300 font-mono">yes</span>. Separate multiple photo URLs with <span className="font-mono">|</span>. Max 300 rows.
            </p>
            <textarea
              data-testid="shop-csv-input"
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder={"title,price_xrp,quantity,grade,limited\nCharizard Holo,45,1,PSA 9,\nFuzzy Bear 1st Ed,30,4,,yes"}
              rows={6}
              className="w-full rounded-lg bg-slate-900 border border-slate-700 text-white text-[11px] p-2 font-mono placeholder:text-slate-600"
            />
            <Button data-testid="shop-csv-submit" size="sm" onClick={importCsv} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500 h-9 text-xs">
              {busy ? 'Importing…' : 'Import cards'}
            </Button>
          </div>
        )}
        {showConfig && cfgForm && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Flat shipping per order (XRP)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[9px] text-slate-500 mb-1">Domestic</p>
                <Input data-testid="cfg-ship-domestic" type="number" value={cfgForm.shipping_domestic} onChange={e => setCfgForm(f => ({ ...f, shipping_domestic: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <p className="text-[9px] text-slate-500 mb-1">International</p>
                <Input data-testid="cfg-ship-intl" type="number" value={cfgForm.shipping_international} onChange={e => setCfgForm(f => ({ ...f, shipping_international: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase pt-1">Bulk discount tiers (limited cards excluded)</p>
            <div className="grid grid-cols-4 gap-2 items-center">
              <Input data-testid="cfg-t1-qty" type="number" value={cfgForm.t1_qty} onChange={e => setCfgForm(f => ({ ...f, t1_qty: e.target.value }))} className={inputCls} />
              <span className="text-[10px] text-slate-500">+ cards →</span>
              <Input data-testid="cfg-t1-pct" type="number" value={cfgForm.t1_pct} onChange={e => setCfgForm(f => ({ ...f, t1_pct: e.target.value }))} className={inputCls} />
              <span className="text-[10px] text-slate-500">% off</span>
              <Input data-testid="cfg-t2-qty" type="number" value={cfgForm.t2_qty} onChange={e => setCfgForm(f => ({ ...f, t2_qty: e.target.value }))} className={inputCls} />
              <span className="text-[10px] text-slate-500">+ cards →</span>
              <Input data-testid="cfg-t2-pct" type="number" value={cfgForm.t2_pct} onChange={e => setCfgForm(f => ({ ...f, t2_pct: e.target.value }))} className={inputCls} />
              <span className="text-[10px] text-slate-500">% off</span>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase pt-1">Community seller commission</p>
            <div className="grid grid-cols-4 gap-1.5">
              {['9.85', '5.89', '3.21', '1.23'].map(p => (
                <button
                  key={p}
                  data-testid={`cfg-seller-fee-${p.replace('.', '-')}`}
                  onClick={() => setCfgForm(f => ({ ...f, seller_fee_pct: p }))}
                  className={`rounded-lg border py-2 text-[11px] font-bold ${parseFloat(cfgForm.seller_fee_pct) === parseFloat(p) ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'bg-slate-900 border-slate-700 text-slate-400'}`}
                >{p}%</button>
              ))}
            </div>
            <p className="text-[9px] text-slate-500">Taken from the seller's payout on every community card sale.</p>
            <Button data-testid="cfg-save" size="sm" onClick={saveConfig} disabled={busy} className="w-full bg-slate-600 hover:bg-slate-500 h-9 text-xs">
              {busy ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        )}
        {showForm && (
          <div className="space-y-2">
            <Input data-testid="shop-form-title" placeholder="Card title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} />
            <Input data-testid="shop-form-desc" placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputCls} />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Condition" value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))} className={inputCls} />
              <Input placeholder="Grade (PSA 10)" value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} className={inputCls} />
              <Input placeholder="Cert #" value={form.cert_number} onChange={e => setForm(f => ({ ...f, cert_number: e.target.value }))} className={inputCls} />
            </div>
            <Input placeholder="Photo URLs (comma separated)" value={form.photo_urls} onChange={e => setForm(f => ({ ...f, photo_urls: e.target.value }))} className={inputCls} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="shop-form-price" type="number" placeholder="Price (XRP) *" value={form.price_xrp} onChange={e => setForm(f => ({ ...f, price_xrp: e.target.value }))} className={inputCls} />
              <Input data-testid="shop-form-quantity" type="number" placeholder="Quantity" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className={inputCls} />
            </div>
            <button
              type="button"
              data-testid="shop-form-limited-toggle"
              onClick={() => setForm(f => ({ ...f, is_limited: !f.is_limited }))}
              className={`w-full flex items-center justify-between rounded-lg border p-2 text-xs font-bold ${form.is_limited ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' : 'bg-slate-900 border-slate-700 text-slate-300'}`}
            >
              {form.is_limited ? '✨ LIMITED — excluded from bulk discount' : 'Mark as Limited (no bulk discount)'}
              <span className={`w-9 h-4.5 rounded-full relative ${form.is_limited ? 'bg-amber-500' : 'bg-slate-700'}`}>
                <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${form.is_limited ? 'left-[20px]' : 'left-0.5'}`} />
              </span>
            </button>
            <button
              type="button"
              data-testid="shop-form-flip-switch"
              onClick={() => setForm(f => ({ ...f, accept_fuzzy: !f.accept_fuzzy }))}
              className={`w-full flex items-center justify-between rounded-lg border p-2 text-xs font-bold ${form.accept_fuzzy ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' : 'bg-slate-900 border-slate-700 text-slate-300'}`}
            >
              {form.accept_fuzzy ? '🍯 Also accepts FUZZY — buyer flips the switch' : 'Flip the Switch: also accept FUZZY'}
              <span className={`w-9 h-4.5 rounded-full relative ${form.accept_fuzzy ? 'bg-amber-500' : 'bg-slate-700'}`}>
                <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${form.accept_fuzzy ? 'left-[20px]' : 'left-0.5'}`} />
              </span>
            </button>
            {form.accept_fuzzy && (
              <Input data-testid="shop-form-price-fuzzy" type="number" placeholder="Price (FUZZY) *" value={form.price_fuzzy} onChange={e => setForm(f => ({ ...f, price_fuzzy: e.target.value }))} className={inputCls} />
            )}
            <Button data-testid="shop-form-submit" size="sm" onClick={createListing} disabled={busy} className="w-full bg-cyan-600 hover:bg-cyan-500 h-9 text-xs">
              {busy ? 'Creating…' : 'Create listing'}
            </Button>
          </div>
        )}

        {pendingApproval.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-amber-300 uppercase">⏳ Seller listings awaiting approval ({pendingApproval.length})</p>
            {pendingApproval.map(l => (
              <div key={l.listing_id} data-testid={`pending-listing-${l.listing_id.slice(0, 8)}`} className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  {l.photo_urls?.[0] && <img src={l.photo_urls[0]} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-700" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-white font-semibold truncate">{l.title}</p>
                    <p className="text-[10px] text-slate-500">
                      @{l.seller_username} · {l.payment_mode === 'fuzzy' ? `${Number(l.price_fuzzy).toLocaleString()} FUZZY` : `${l.price_xrp} XRP`}
                      {l.payment_mode === 'both' && ` / ${Number(l.price_fuzzy).toLocaleString()} FUZZY`} · qty {l.quantity}
                      {l.allow_offers === false ? ' · no offers' : ' · offers ok'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid={`approve-listing-${l.listing_id.slice(0, 8)}`} size="sm" disabled={busy} onClick={() => approveListing(l.listing_id)} className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs">Approve</Button>
                  <Button data-testid={`reject-listing-${l.listing_id.slice(0, 8)}`} size="sm" variant="outline" disabled={busy} onClick={() => rejectListing(l.listing_id)} className="border-red-800/60 text-red-300 h-8 text-xs">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {active.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Active listings ({active.length})</p>
            {active.map(l => (
              <div key={l.listing_id} className="flex items-center justify-between bg-slate-900/60 rounded-lg px-2.5 py-2">
                <div className="min-w-0">
                  <p className="text-xs text-white font-semibold truncate">
                    {l.title}
                    {l.is_limited && <span className="ml-1.5 text-[8px] font-extrabold px-1 py-0.5 rounded bg-amber-500/90 text-slate-900 align-middle">LTD</span>}
                  </p>
                  <p className="text-[10px] text-slate-500">{l.price_xrp} XRP · qty {l.quantity ?? 1}{l.accept_fuzzy ? ` · ${Number(l.price_fuzzy).toLocaleString()} FUZZY` : ''}</p>
                </div>
                <button data-testid={`shop-remove-${l.listing_id.slice(0,8)}`} onClick={() => removeListing(l.listing_id)} className="text-[10px] text-red-400 hover:text-red-300 font-semibold flex-shrink-0 ml-2">Remove</button>
              </div>
            ))}
          </div>
        )}

        {(data?.pending_offers || []).length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-amber-400 uppercase">Pending offers ({data.pending_offers.length})</p>
            {data.pending_offers.map(o => (
              <div key={o.offer_id} className="bg-slate-900/60 rounded-lg px-2.5 py-2 space-y-1.5">
                <p className="text-xs text-white"><span className="font-semibold">{o.buyer_username}</span> offers <span className="text-amber-300 font-bold">{Number(o.amount).toLocaleString()} {o.currency}</span> on {o.listing_title}</p>
                <div className="flex gap-2">
                  <Button data-testid={`offer-accept-${o.offer_id.slice(0,8)}`} size="sm" onClick={() => resolveOffer(o.offer_id, 'accept')} className="flex-1 h-7 text-[10px] bg-emerald-700 hover:bg-emerald-600">Accept</Button>
                  <Button data-testid={`offer-decline-${o.offer_id.slice(0,8)}`} size="sm" variant="outline" onClick={() => resolveOffer(o.offer_id, 'decline')} className="flex-1 h-7 text-[10px] border-red-800/60 text-red-300 bg-transparent hover:bg-red-900/20">Decline</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {(data?.escrows || []).length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-emerald-400 uppercase">Orders ({data.escrows.length})</p>
            {data.escrows.map(e => (
              <div key={e.escrow_id} className="bg-slate-900/60 rounded-lg px-2.5 py-2 space-y-1.5">
                <p className="text-xs text-white font-semibold">{e.prize_description} — {Number(e.escrow_amount).toLocaleString()} {e.currency || 'XRP'}</p>
                <p className="text-[10px] text-slate-500">Status: {e.status}{e.tracking_number ? ` · Tracking ${e.tracking_number}` : ''}{!e.shipping_info && e.status === 'pending_shipment' ? ' · awaiting buyer address' : ''}</p>
                {e.status === 'pending_shipment' && e.shipping_info && (
                  <div className="flex gap-2">
                    <Input placeholder="Tracking #" value={trackingDrafts[e.escrow_id] || ''} onChange={ev => setTrackingDrafts(d => ({ ...d, [e.escrow_id]: ev.target.value }))} className="flex-1 h-7 text-[10px] bg-slate-900 border-slate-700 text-white" />
                    <Button data-testid={`shop-ship-${e.escrow_id.slice(0,8)}`} size="sm" onClick={() => shipEscrow(e.escrow_id)} className="h-7 text-[10px] bg-emerald-700 hover:bg-emerald-600">Mark shipped</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {sold.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-slate-500 uppercase">Recently sold</p>
            {sold.map(l => (
              <p key={l.listing_id} className="text-[10px] text-slate-400">✓ {l.title} → {l.buyer_username} for {Number(l.sold_price).toLocaleString()} {l.sold_currency}{l.sold_via_offer ? ' (offer)' : ''}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
